import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Copy, FileCheck2, Loader2, Plus, Trash2, UserPlus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import GoogleMapLocationPicker from '../../components/shared/GoogleMapLocationPicker';
import { NIGERIA_STATES, getLgasForState, isValidLgaForState } from '../../lib/nigeriaLocations';
import { getApiErrorMessage } from '../../lib/apiError';

const today = () => new Date().toISOString().slice(0, 10);
const newUploadSessionId = () => (
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `dep-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const medicalConditions = [
  'Diabetes', 'Hypertension', 'Heart Condition', 'Stroke History', 'Kidney Disease', 'Liver Disease',
  'Arthritis/Joint', 'Respiratory (Asthma/COPD)', 'Cancer', 'Sickle Cell', 'Dementia/Alzheimer\'s',
  'Parkinson\'s', 'Depression/Anxiety', 'Epilepsy/Seizures', 'Visual Impairment', 'Hearing Impairment'
];
const dietaryOptions = ['None', 'Diabetic Diet', 'Low Sodium', 'Low Cholesterol', 'Vegetarian', 'Other'];
const caregiverLanguageOptions = ['English', 'Yoruba', 'Igbo', 'Hausa', 'Pidgin', 'No Preference'];

const initialForm = {
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  age: '',
  gender: '',
  relationshipToSubscriber: '',
  relationshipOther: '',
  primaryLanguage: '',
  religion: '',
  street: '',
  neighbourhood: '',
  lga: '',
  state: '',
  landmark: '',
  coordinates: null,
  formattedAddress: '',
  placeId: '',
  locationSource: '',
  locationConfirmedAt: '',
  propertyType: '',
  livingSituation: '',
  primaryPhone: '',
  secondaryPhone: '',
  whatsappNumber: '',
  smartphoneCapability: '',
  nin: '',
  validIdUpload: null,
  passportPhotoUpload: null,
  generalHealthStatus: '',
  medicalConditions: [],
  terminalConditions: '',
  medications: [],
  drugAllergies: '',
  doctorName: '',
  doctorPhone: '',
  doctorHospital: '',
  mobilityStatus: '',
  cognitiveStatus: '',
  vision: '',
  dietaryRequirements: [],
  propertyGated: '',
  backupGenerator: '',
  waterSource: '',
  preferredHospital: '',
  hospitalAddress: '',
  hospitalPhone: '',
  caregiverGender: '',
  caregiverLanguages: [],
  consentInfoSharing: false,
  consentEmergencyMedical: false,
  consentHomeAccess: false,
  consentAccuracy: false,
  consentTerms: false,
  consentFullName: '',
  consentSignature: '',
  consentDate: today()
};

const sections = [
  {
    title: 'Personal Information',
    fields: [
      { key: 'title', label: 'Title', type: 'select', options: ['Mr', 'Mrs', 'Ms', 'Chief', 'Pastor', 'Other'] },
      { key: 'firstName', label: 'First Name', required: true },
      { key: 'middleName', label: 'Middle Name' },
      { key: 'lastName', label: 'Last Name', required: true },
      { key: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
      { key: 'age', label: 'Age', type: 'number', required: true },
      { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female'], required: true },
      { key: 'relationshipToSubscriber', label: 'Relationship to Subscriber', type: 'select', options: ['Father', 'Mother', 'Grandfather', 'Grandmother', 'Uncle', 'Aunt', 'Other'], required: true },
      { key: 'relationshipOther', label: 'If Other, specify' },
      { key: 'primaryLanguage', label: 'Primary Language', type: 'select', options: ['English', 'Yoruba', 'Igbo', 'Hausa', 'Other'], required: true },
      { key: 'religion', label: 'Religion / Faith', type: 'select', options: ['Christianity', 'Islam', 'Traditional', 'Other'] }
    ]
  },
  {
    title: 'Residential Address',
    fields: [
      { key: 'street', label: 'House Number & Street', required: true },
      { key: 'neighbourhood', label: 'Area / Neighbourhood', required: true },
      { key: 'state', label: 'State', type: 'state', required: true },
      { key: 'lga', label: 'Local Government Area (LGA)', type: 'lga', required: true },
      { key: 'landmark', label: 'Nearest Landmark' },
      { key: 'propertyType', label: 'Type of Property', type: 'select', options: ['Bungalow', 'Flat/Apartment', 'Duplex', 'Compound', 'Other'], required: true },
      { key: 'livingSituation', label: 'Living Situation', type: 'select', options: ['Lives Alone', 'With Family Member', 'With Househelp', 'With Spouse', 'Other'], required: true }
    ]
  },
  {
    title: 'Contact & Identity',
    fields: [
      { key: 'primaryPhone', label: 'Primary Phone Number', type: 'tel', required: true },
      { key: 'secondaryPhone', label: 'Secondary Phone Number', type: 'tel' },
      { key: 'whatsappNumber', label: 'WhatsApp Number', type: 'tel' },
      { key: 'smartphoneCapability', label: 'Can use smartphone independently?', type: 'select', options: ['Yes', 'No', 'With Assistance'], required: true },
      { key: 'nin', label: 'NIN', required: true, hint: 'Enter exactly 11 digits.' },
      { key: 'validIdUpload', label: 'Upload: Valid ID', type: 'file', required: true },
      { key: 'passportPhotoUpload', label: 'Upload: Recent Passport Photograph', type: 'file', required: true }
    ]
  },
  {
    title: 'Health Profile',
    fields: [
      { key: 'generalHealthStatus', label: 'General Health Status', type: 'select', options: ['Good', 'Fair', 'Poor'], required: true },
      { key: 'medicalConditions', label: 'Existing Medical Conditions', type: 'multi', options: medicalConditions },
      { key: 'terminalConditions', label: 'Any Terminal Conditions', required: true, hint: 'Enter None if not applicable.' },
      { key: 'drugAllergies', label: 'Known Drug Allergies', required: true, hint: 'Enter None if not applicable.' },
      { key: 'doctorName', label: 'Name of Personal/Family Doctor', required: true },
      { key: 'doctorPhone', label: 'Doctor\'s Phone Number', type: 'tel', required: true },
      { key: 'doctorHospital', label: 'Doctor\'s Clinic/Hospital Name', required: true }
    ]
  },
  {
    title: 'Mobility & Home',
    fields: [
      { key: 'mobilityStatus', label: 'Mobility Status', type: 'select', options: ['Fully Mobile', 'Partially Mobile', 'Uses Walking Aid', 'Wheelchair Bound', 'Bedridden'], required: true },
      { key: 'cognitiveStatus', label: 'Cognitive/Mental Status', type: 'select', options: ['Fully Alert', 'Mild Memory Issues', 'Moderate Decline', 'Dementia Diagnosed'], required: true },
      { key: 'vision', label: 'Vision', type: 'select', options: ['Normal', 'Impaired (wears glasses)', 'Severely Impaired', 'Blind'], required: true },
      { key: 'dietaryRequirements', label: 'Special Dietary Requirements', type: 'multi', options: dietaryOptions, required: true },
      { key: 'propertyGated', label: 'Is Property Gated?', type: 'select', options: ['Yes', 'No'], required: true },
      { key: 'backupGenerator', label: 'Backup Generator Available?', type: 'select', options: ['Yes', 'No'], required: true },
      { key: 'waterSource', label: 'Primary Water Source', type: 'select', options: ['Borehole', 'PHCN/Waterboard', 'Both', 'Tanker Supply', 'Other'], required: true }
    ]
  },
  {
    title: 'Hospital & Caregiver',
    fields: [
      { key: 'preferredHospital', label: 'Preferred Hospital/Clinic Name', required: true },
      { key: 'hospitalAddress', label: 'Hospital Address', type: 'textarea', required: true },
      { key: 'hospitalPhone', label: 'Hospital Phone Number', type: 'tel', required: true },
      { key: 'caregiverGender', label: 'Preferred Caregiver Gender', type: 'select', options: ['Female', 'Male', 'No Preference'], required: true },
      { key: 'caregiverLanguages', label: 'Preferred Caregiver Language(s)', type: 'multi', options: caregiverLanguageOptions, required: true }
    ]
  },
  {
    title: 'Consent',
    fields: [
      { key: 'consentInfoSharing', label: 'Authorise info sharing with caregivers/vendors/emergency responders', type: 'checkbox', required: true },
      { key: 'consentEmergencyMedical', label: 'Authorise emergency medical decisions if subscriber unreachable', type: 'checkbox', required: true },
      { key: 'consentHomeAccess', label: 'Authorise home access for care/emergency', type: 'checkbox', required: true },
      { key: 'consentAccuracy', label: 'Confirm all info is accurate', type: 'checkbox', required: true },
      { key: 'consentTerms', label: 'Agree to Terms of Service & Privacy Policy', type: 'checkbox', required: true },
      { key: 'consentFullName', label: 'Subscriber Full Name', required: true },
      { key: 'consentSignature', label: 'Subscriber Signature', required: true },
      { key: 'consentDate', label: 'Date', type: 'date', required: true, disabled: true }
    ]
  }
];

const requiredFields = sections.flatMap(section => section.fields.filter(field => field.required));

const isFilled = (value, field) => {
  if (field.type === 'checkbox') return value === true;
  if (field.type === 'file') return Boolean(value?.fileUrl);
  if (field.type === 'multi') return Array.isArray(value) && value.length > 0;
  if (value === 0) return true;
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
};

const normalizeCoordinates = (value) => {
  if (!value) return null;
  const raw = value.coordinates || value;
  const latitude = raw.latitude ?? raw.lat;
  const longitude = raw.longitude ?? raw.lng;
  if (latitude === undefined || longitude === undefined) return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  if (lat < 4 || lat > 14.5 || lng < 2 || lng > 15.5) return null;
  return { latitude: lat, longitude: lng, lat, lng };
};

const camelToSnake = (value) => value.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

const getValue = (source, key, fallback = '') => {
  if (!source) return fallback;
  const snake = camelToSnake(key);
  return source[key] ?? source[snake] ?? fallback;
};

const formFromDependant = (dependant) => {
  const address = dependant?.address || {};
  const next = { ...initialForm };
  Object.keys(initialForm).forEach((key) => {
    const value = getValue(dependant, key, undefined);
    if (value !== undefined && value !== null) {
      next[key] = value;
    }
  });
  ['street', 'neighbourhood', 'lga', 'state', 'landmark'].forEach((key) => {
    next[key] = getValue(dependant, key, getValue(address, key, next[key]));
  });
  next.primaryPhone = getValue(dependant, 'primaryPhone', dependant?.phone || dependant?.primary_phone || '');
  next.relationshipToSubscriber = getValue(
    dependant,
    'relationshipToSubscriber',
    dependant?.relationship || dependant?.relationship_to_subscriber || ''
  );
  next.validIdUpload = getValue(dependant, 'validIdUpload', dependant?.valid_id_upload || null);
  next.passportPhotoUpload = getValue(dependant, 'passportPhotoUpload', dependant?.passport_photo_upload || null);
  next.medicalConditions = Array.isArray(next.medicalConditions) ? next.medicalConditions : [];
  next.dietaryRequirements = Array.isArray(next.dietaryRequirements) ? next.dietaryRequirements : [];
  next.caregiverLanguages = Array.isArray(next.caregiverLanguages) ? next.caregiverLanguages : [];
  next.medications = Array.isArray(next.medications) ? next.medications : [];
  next.coordinates = normalizeCoordinates(
    dependant?.coordinates ||
    address.coordinates ||
    {
      latitude: address.latitude,
      longitude: address.longitude,
    }
  );
  next.formattedAddress = getValue(dependant, 'formattedAddress', getValue(address, 'formattedAddress', ''));
  next.placeId = getValue(dependant, 'placeId', getValue(address, 'placeId', ''));
  next.locationSource = getValue(dependant, 'locationSource', getValue(address, 'locationSource', ''));
  next.locationConfirmedAt = getValue(dependant, 'locationConfirmedAt', getValue(address, 'locationConfirmedAt', ''));
  next.consentDate = next.consentDate || today();
  return next;
};

const AddDependentPage = () => {
  const navigate = useNavigate();
  const { dependantId } = useParams();
  const { authApi } = useAuth();
  const { fetchData, upsertDependant } = useApp();
  const isEditing = Boolean(dependantId);
  const [form, setForm] = useState(initialForm);
  const [uploadSessionId] = useState(newUploadSessionId);
  const [dependentCount, setDependentCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(true);
  const [loadingDependent, setLoadingDependent] = useState(Boolean(dependantId));
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [serverMissing, setServerMissing] = useState([]);
  const [activationResult, setActivationResult] = useState(null);

  useEffect(() => {
    let mounted = true;
    const loadCount = async () => {
      try {
        const response = await authApi.get('/dependants');
        const dependants = response.data || [];
        if (mounted) {
          setDependentCount(isEditing ? dependants.filter(item => item.id !== dependantId && item._id !== dependantId).length : dependants.length);
        }
      } catch (err) {
        if (mounted) setError(getApiErrorMessage(err, 'Unable to check dependent limit.'));
      } finally {
        if (mounted) setLoadingCount(false);
      }
    };
    loadCount();
    return () => {
      mounted = false;
    };
  }, [authApi, dependantId, isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setLoadingDependent(false);
      setForm(initialForm);
      return undefined;
    }
    let mounted = true;
    const loadDependant = async () => {
      setLoadingDependent(true);
      setError('');
      try {
        const response = await authApi.get(`/dependants/${dependantId}`);
        if (mounted) setForm(formFromDependant(response.data));
      } catch (err) {
        if (mounted) setError(getApiErrorMessage(err, 'Unable to load this dependant for editing.'));
      } finally {
        if (mounted) setLoadingDependent(false);
      }
    };
    loadDependant();
    return () => {
      mounted = false;
    };
  }, [authApi, dependantId, isEditing]);

  const missingFields = useMemo(() => {
    const missing = requiredFields
      .filter(field => !isFilled(form[field.key], field))
      .map(field => field.label);
    if (form.relationshipToSubscriber === 'Other' && !form.relationshipOther.trim()) {
      missing.push('If Other, specify');
    }
    if (form.nin && !/^\d{11}$/.test(form.nin)) {
      missing.push('NIN must be exactly 11 digits');
    }
    if (!normalizeCoordinates(form.coordinates)) {
      missing.push('Set exact home location in Nigeria');
    }
    return missing;
  }, [form]);

  const updateField = (key, value) => {
    setForm(prev => {
      if (key === 'state') {
        return {
          ...prev,
          state: value,
          lga: isValidLgaForState(value, prev.lga) ? prev.lga : ''
        };
      }
      return { ...prev, [key]: value };
    });
    setServerMissing([]);
  };

  const updateLocation = useCallback((location) => {
    const coordinates = normalizeCoordinates(location?.coordinates || location);
    setForm(prev => ({
      ...prev,
      coordinates,
      formattedAddress: location.formattedAddress || '',
      placeId: location.placeId || '',
      locationSource: location.locationSource || 'google_maps_picker',
      locationConfirmedAt: location.locationConfirmedAt || new Date().toISOString()
    }));
    setServerMissing([]);
  }, []);

  const toggleArrayValue = (key, option) => {
    setForm(prev => {
      const current = prev[key] || [];
      const next = current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option];
      return { ...prev, [key]: next };
    });
  };

  const addMedication = () => {
    setForm(prev => ({
      ...prev,
      medications: [...prev.medications, { name: '', dosage: '', frequency: '', prescribingDoctor: '' }]
    }));
  };

  const updateMedication = (index, key, value) => {
    setForm(prev => ({
      ...prev,
      medications: prev.medications.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      ))
    }));
  };

  const removeMedication = (index) => {
    setForm(prev => ({
      ...prev,
      medications: prev.medications.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const uploadFile = async (field, file) => {
    if (!file) return;
    setError('');

    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    const extensionAllowed = /\.(jpe?g|png|pdf)$/i.test(file.name);
    if (!allowed.includes(file.type) && !extensionAllowed) {
      setError('Upload .jpg, .jpeg, .png, or .pdf files only.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Each upload must be 5MB or smaller.');
      return;
    }

    const data = new FormData();
    data.append('fieldName', field.key);
    data.append('file', file);

    setUploading(prev => ({ ...prev, [field.key]: true }));
    setUploadProgress(prev => ({ ...prev, [field.key]: 0 }));
    try {
      const uploadTarget = isEditing ? dependantId : uploadSessionId;
      const response = await authApi.post(`/dependants/${uploadTarget}/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          if (event.total) {
            setUploadProgress(prev => ({ ...prev, [field.key]: Math.round((event.loaded * 100) / event.total) }));
          }
        }
      });
      updateField(field.key, response.data);
    } catch (err) {
      setError(getApiErrorMessage(err, `Unable to upload ${field.label}.`));
    } finally {
      setUploading(prev => ({ ...prev, [field.key]: false }));
    }
  };

  const submitDependent = async (event) => {
    event.preventDefault();
    setError('');
    setServerMissing([]);

    if (!isEditing && dependentCount >= 4) {
      setError('You can register a maximum of 4 dependents.');
      return;
    }

    if (missingFields.length > 0) {
      setServerMissing(missingFields);
      setError('Complete all mandatory fields before registering this dependent.');
      return;
    }

    setSubmitting(true);
    try {
      const coordinates = normalizeCoordinates(form.coordinates);
      const payload = {
        ...form,
        coordinates,
        age: Number(form.age),
        ...(isEditing ? {} : { uploadSessionId }),
        medications: form.medications.filter(item => item.name.trim()),
        address: {
          street: form.street,
          neighbourhood: form.neighbourhood,
          lga: form.lga,
          state: form.state,
          country: 'Nigeria',
          landmark: form.landmark,
          coordinates,
          formattedAddress: form.formattedAddress,
          formatted_address: form.formattedAddress,
          placeId: form.placeId,
          place_id: form.placeId,
          locationSource: form.locationSource,
          location_source: form.locationSource,
          locationConfirmedAt: form.locationConfirmedAt,
          location_confirmed_at: form.locationConfirmedAt
        }
      };
      const response = isEditing
        ? await authApi.patch(`/dependants/${dependantId}`, payload)
        : await authApi.post('/dependants', payload);
      upsertDependant(response.data);
      const temporaryCredentials = response.data?.temporaryCredentials || response.data?.activation?.temporaryCredentials;
      if (!isEditing && temporaryCredentials) {
        setActivationResult({
          dependantName: `${form.firstName} ${form.lastName}`.trim() || 'Dependant',
          credentials: temporaryCredentials,
        });
        fetchData({ force: true, background: true });
        return;
      }
      if (!isEditing && response.data?.activation?.activationUrl) {
        setActivationResult({
          dependantName: `${form.firstName} ${form.lastName}`.trim() || 'Dependant',
          url: response.data.activation.activationUrl,
          queued: response.data.activation.activationEmailQueued,
        });
        fetchData({ force: true, background: true });
        return;
      }
      await fetchData({ force: true, background: true });
      navigate('/dependants', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.missingFields) {
        setServerMissing(detail.missingFields);
        setError(detail.message || 'Dependent registration is incomplete.');
      } else {
        setError(getApiErrorMessage(err, isEditing ? 'Unable to update dependent.' : 'Unable to register dependent.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = form[field.key];
    const isMissing = serverMissing.includes(field.label);
    const commonClass = isMissing ? 'border-red-300 focus-visible:ring-red-400' : '';

    if (field.type === 'state') {
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={(nextValue) => updateField(field.key, nextValue)}>
            <SelectTrigger className={commonClass}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {NIGERIA_STATES.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === 'lga') {
      const lgas = getLgasForState(form.state);
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={(nextValue) => updateField(field.key, nextValue)} disabled={!form.state || lgas.length === 0}>
            <SelectTrigger className={commonClass}>
              <SelectValue placeholder={form.state ? 'Select LGA' : 'Select state first'} />
            </SelectTrigger>
            <SelectContent>
              {lgas.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={(nextValue) => updateField(field.key, nextValue)}>
            <SelectTrigger className={commonClass}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === 'multi') {
      return (
        <div className="space-y-2 sm:col-span-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <div className={`grid gap-2 rounded-lg border ${isMissing ? 'border-red-300' : 'border-[#E8E6E3]'} bg-[#FAFAF7] p-4 sm:grid-cols-2 lg:grid-cols-3`}>
            {field.options.map(option => (
              <label key={option} className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                <Checkbox checked={(value || []).includes(option)} onCheckedChange={() => toggleArrayValue(field.key, option)} />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div className="space-y-2 sm:col-span-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Textarea value={value || ''} onChange={(event) => updateField(field.key, event.target.value)} className={commonClass} />
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label key={field.key} className={`flex items-start gap-3 rounded-lg border ${isMissing ? 'border-red-300' : 'border-[#E8E6E3]'} bg-[#FAFAF7] p-4 text-sm text-[#1A1A1A] sm:col-span-2`}>
          <Checkbox checked={Boolean(value)} onCheckedChange={(checked) => updateField(field.key, Boolean(checked))} />
          <span>{field.label}{field.required ? ' *' : ''}</span>
        </label>
      );
    }

    if (field.type === 'file') {
      const uploaded = Boolean(value?.fileUrl);
      return (
        <div className="space-y-2 sm:col-span-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <div className={`rounded-lg border ${isMissing ? 'border-red-300' : 'border-[#E8E6E3]'} bg-[#FAFAF7] p-4`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${uploaded ? 'bg-green-100 text-green-700' : 'bg-[#F5E8EA] text-[#9F1D35]'}`}>
                  <FileCheck2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">{uploaded ? value.fileName : 'No file uploaded yet'}</p>
                  <p className="text-xs text-[#5A5A5A]">JPG, PNG, or PDF. Max 5MB.</p>
                </div>
              </div>
              <Input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(event) => uploadFile(field, event.target.files?.[0])} className="sm:max-w-[260px]" />
            </div>
            {uploading[field.key] && (
              <div className="mt-3">
                <Progress value={uploadProgress[field.key] || 15} />
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2" key={field.key}>
        <Label>{field.label}{field.required ? ' *' : ''}</Label>
        <Input
          type={field.type || 'text'}
          value={value ?? ''}
          disabled={field.disabled}
          onChange={(event) => updateField(field.key, event.target.value)}
          className={commonClass}
        />
        {field.hint && <p className="text-xs text-[#5A5A5A]">{field.hint}</p>}
      </div>
    );
  };

  const anyUploading = Object.values(uploading).some(Boolean);
  const canSubmit = missingFields.length === 0 && (isEditing || dependentCount < 4) && !anyUploading && !loadingCount && !loadingDependent;
  const addressHint = [
    form.street,
    form.neighbourhood,
    form.landmark,
    form.lga,
    form.state,
    'Nigeria'
  ].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-[#F7F4EE] px-4 py-8">
      {activationResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1A1A]/50 px-4">
          <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-6">
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#F5E8EA] text-[#9F1D35]">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Dependant registered</h2>
            {activationResult.credentials ? (
              <>
                <p className="mt-2 text-sm leading-6 text-[#5A5A5A]">
                  Copy these login details for {activationResult.dependantName}. This temporary password is shown once.
                </p>
                <div className="mt-4 space-y-3 rounded-xl border border-[#E8E6E3] bg-[#F7F4EE] p-4">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Login phone</p>
                    <p className="break-all text-sm font-semibold text-[#1A1A1A]">{activationResult.credentials.loginIdentifier}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Temporary password</p>
                    <p className="break-all text-sm font-semibold text-[#9F1D35]">{activationResult.credentials.temporaryPassword}</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm leading-6 text-[#5A5A5A]">
                  Use this activation URL to complete {activationResult.dependantName}'s account setup.
                </p>
                <div className="mt-4 rounded-xl border border-[#E8E6E3] bg-[#F7F4EE] p-3">
                  <p className="break-all text-sm font-medium text-[#9F1D35]">{activationResult.url}</p>
                </div>
              </>
            )}
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(
                  activationResult.credentials
                    ? `Login phone: ${activationResult.credentials.loginIdentifier}\nTemporary password: ${activationResult.credentials.temporaryPassword}`
                    : activationResult.url
                )}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#9F1D35] px-5 py-3 text-sm font-medium text-[#9F1D35]"
              >
                <Copy className="h-4 w-4" />
                Copy details
              </button>
              {!activationResult.credentials && (
                <a
                  href={activationResult.url}
                  className="inline-flex items-center justify-center rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] hover:bg-[#7A1628]"
                >
                  Open setup link
                </a>
              )}
              <button
                type="button"
                onClick={() => navigate('/dependants', { replace: true })}
                className="inline-flex items-center justify-center rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] hover:bg-[#7A1628]"
              >
                Back to dependants
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link to="/dependants" className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#9F1D35]">
              <ArrowLeft className="h-4 w-4" />
              Back to dependents
            </Link>
            <h1 className="font-serif text-3xl font-medium text-[#1A1A1A]">{isEditing ? 'Edit dependent' : 'Register a dependent'}</h1>
            <p className="mt-1 text-sm text-[#5A5A5A]">{dependentCount}/4 dependents registered</p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#F5E8EA] text-[#9F1D35]">
            <UserPlus className="h-6 w-6" />
          </div>
        </div>

        {!isEditing && dependentCount >= 4 && (
          <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            You have reached the maximum of 4 dependents for this subscriber account.
          </div>
        )}

        {error && (
          <div className="mb-5 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            <p>{error}</p>
            {serverMissing.length > 0 && <p className="mt-2 text-xs">{serverMissing.join(', ')}</p>}
          </div>
        )}

        {loadingDependent ? (
          <div className="rounded-lg border border-[#E8E6E3] bg-white p-6 text-sm text-[#5A5A5A]">Loading dependent details...</div>
        ) : (
        <form onSubmit={submitDependent} className="space-y-5">
          {sections.map(section => (
            <section key={section.title} className="rounded-lg border border-[#E8E6E3] bg-white p-6">
              <h2 className="mb-5 font-serif text-2xl font-medium text-[#1A1A1A]">{section.title}</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {section.fields.map(renderField)}
                {section.title === 'Residential Address' && (
                  <GoogleMapLocationPicker
                    value={{
                      coordinates: form.coordinates,
                      formattedAddress: form.formattedAddress,
                      placeId: form.placeId,
                    }}
                    addressHint={addressHint}
                    onChange={updateLocation}
                    required
                  />
                )}
              </div>

              {section.title === 'Health Profile' && (
                <div className="mt-5 rounded-lg border border-[#E8E6E3] bg-[#FAFAF7] p-4">
                  <div className="mb-4 flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-[#1A1A1A]">Current Medications</h3>
                      <p className="text-xs text-[#5A5A5A]">Optional, add each medication separately.</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addMedication}>
                      <Plus className="h-4 w-4" />
                      Add
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {form.medications.map((medication, index) => (
                      <div key={index} className="grid gap-3 rounded-lg border border-[#E8E6E3] bg-white p-3 sm:grid-cols-4">
                        <Input placeholder="Name" value={medication.name} onChange={(event) => updateMedication(index, 'name', event.target.value)} />
                        <Input placeholder="Dosage" value={medication.dosage} onChange={(event) => updateMedication(index, 'dosage', event.target.value)} />
                        <Input placeholder="Frequency" value={medication.frequency} onChange={(event) => updateMedication(index, 'frequency', event.target.value)} />
                        <div className="flex gap-2">
                          <Input placeholder="Doctor" value={medication.prescribingDoctor} onChange={(event) => updateMedication(index, 'prescribingDoctor', event.target.value)} />
                          <Button type="button" variant="outline" size="icon" onClick={() => removeMedication(index)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          ))}

          <div className="sticky bottom-0 rounded-lg border border-[#E8E6E3] bg-white/95 p-4 backdrop-blur">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                {canSubmit ? <CheckCircle2 className="h-4 w-4 text-green-700" /> : <span className="h-2 w-2 rounded-full bg-amber-500" />}
                {canSubmit ? (isEditing ? 'Ready to save' : 'Ready to register') : `${missingFields.length} required item${missingFields.length === 1 ? '' : 's'} remaining`}
              </div>
              <Button type="submit" disabled={!canSubmit || submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEditing ? 'Save Changes' : 'Register Dependent'}
              </Button>
            </div>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default AddDependentPage;
